import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  Backdrop,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ScheduleIcon from "@mui/icons-material/Schedule";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InstagramImageCropper from "../../../Components/InstagramImageCropper";
import CalendarView from "../../../Components/CalendarView";

const SafePostDialogs = (props) => {
  const {
    // Instagram cropper
    instagramCropperOpen,
    handleInstagramCropperClose,
    currentCroppingImage,
    handleInstagramCropSave,

    // Template selection
    templateModalOpen,
    closeTemplateModal,
    templateSearchQuery,
    setTemplateSearchQuery,
    filteredTemplates,
    selectTemplate,

    // Calendar
    calendarOpen,
    closeCalendar,
    calendarDialogContentRef,
    scheduledDateTime,
    handleCalendarDateTimeSelect,
    drawerContainerEl,

    // Preview (lightweight)
    previewModalOpen,
    mediaFile,
    closePreviewModal,
    platforms,
    postContent,
    contentType,
    confirmPosting,

    // Snackbar
    openSnackbar,
    handleCloseSnackbar,
    snackbarSeverity,
    snackbarMessage,

    // Image preview
    imageModalOpen,
    selectedImage,
    closeImageModal,

    // Loader
    showLoader,
    loadingMessage,
    loadingProgress,

    // Confirmation
    showConfirmation,
    onCloseConfirmation,

    // Error modal
    errorModalOpen,
    errorModalData,
    onCloseErrorModal,
  } = props;

  const [previewSrc, setPreviewSrc] = useState("");
  const [mediaPreviews, setMediaPreviews] = useState([]); // NEW

  // Build preview list for the Preview modal
  useEffect(() => {
    let urlsToRevoke = [];

    if (!previewModalOpen) {
      setMediaPreviews([]);
      return;
    }

    const asArray = Array.isArray(mediaFile)
      ? mediaFile
      : mediaFile
      ? [mediaFile]
      : [];

    const isImageByExt = (url = "") =>
      /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(url);
    const isVideoByExt = (url = "") =>
      /\.(mp4|mov|avi|webm|mkv|m4v)$/i.test(url);

    const makeObjectUrl = (blob) => {
      try {
        const u = URL.createObjectURL(blob);
        urlsToRevoke.push(u);
        return u;
      } catch {
        return "";
      }
    };

    const items = asArray.map((f, i) => {
      // Determine kind
      const type = f?.type || "";
      const urlCandidate = f?.s3Url || f?.previewUrl || f?.url || "";
      const kind =
        type.startsWith("image/") || isImageByExt(urlCandidate)
          ? "image"
          : type.startsWith("video/") || isVideoByExt(urlCandidate)
          ? "video"
          : "unknown";

      // Determine src
      let src = urlCandidate;
      if (!src) {
        if (f instanceof File || f instanceof Blob) {
          src = makeObjectUrl(f);
        } else if (f?.file instanceof File || f?.file instanceof Blob) {
          src = makeObjectUrl(f.file);
        }
      }

      return {
        idx: i,
        kind,
        src,
        name: f?.name || f?.originalName || `media-${i}`,
      };
    });

    setMediaPreviews(items.filter((it) => it.src && it.kind !== "unknown"));

    return () => {
      urlsToRevoke.forEach((u) => {
        try {
          URL.revokeObjectURL(u);
        } catch {}
      });
    };
  }, [previewModalOpen, mediaFile]);

  useEffect(() => {
    let objectUrl = null;
    if (!imageModalOpen || !selectedImage) {
      setPreviewSrc("");
      return;
    }

    // If string URL
    if (typeof selectedImage === "string") {
      setPreviewSrc(selectedImage);
      return;
    }

    // If File/Blob
    if (selectedImage instanceof File || selectedImage instanceof Blob) {
      objectUrl = URL.createObjectURL(selectedImage);
      setPreviewSrc(objectUrl);
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    // If object with known fields
    const candidate =
      selectedImage.s3Url ||
      selectedImage.previewUrl ||
      selectedImage.url ||
      null;

    if (candidate) {
      setPreviewSrc(candidate);
      return;
    }

    // If wrapper object contains a File under .file
    if (selectedImage.file instanceof File) {
      objectUrl = URL.createObjectURL(selectedImage.file);
      setPreviewSrc(objectUrl);
      return () => {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
      };
    }

    setPreviewSrc("");
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [imageModalOpen, selectedImage]);

  return (
    <>
      <InstagramImageCropper
        open={instagramCropperOpen}
        onClose={handleInstagramCropperClose}
        image={currentCroppingImage}
        onSave={handleInstagramCropSave}
        fileName={
          currentCroppingImage?.uniqueName || currentCroppingImage?.name
        }
      />

      {/* Template Selection Modal */}
      <Dialog
        open={templateModalOpen}
        onClose={closeTemplateModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#4B5AB6",
            color: "#fff",
          }}
        >
          Choose Post Template
          <IconButton onClick={closeTemplateModal} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <input
              style={{
                width: "100%",
                padding: 12,
                borderRadius: 8,
                border: "1px solid #e5e7eb",
                marginBottom: 16,
              }}
              placeholder="Search templates..."
              value={templateSearchQuery}
              onChange={(e) => setTemplateSearchQuery(e.target.value)}
            />
            <Grid container spacing={2}>
              {filteredTemplates.map((template) => (
                <Grid item xs={12} sm={6} key={template.id}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
                      transition: "box-shadow 0.2s",
                    }}
                    onClick={() => selectTemplate(template)}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {template.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "#64748b", mb: 2 }}
                      >
                        {template.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          bgcolor: "#f8fafc",
                          p: 1,
                          borderRadius: 1,
                          fontSize: "0.85rem",
                          maxHeight: 100,
                          overflow: "hidden",
                        }}
                      >
                        {template.content}
                      </Typography>
                      {template.hashtags && (
                        <Typography
                          variant="caption"
                          sx={{ color: "#4B5AB6", mt: 1, display: "block" }}
                        >
                          {template.hashtags}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog
        open={calendarOpen}
        onClose={closeCalendar}
        fullWidth
        maxWidth="xl"
        keepMounted={false}
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: "#111827",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 1,
          }}
        >
          📅 Schedule Your Post (Requires Approval)
          <IconButton onClick={closeCalendar} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }} ref={calendarDialogContentRef}>
          <CalendarView
            open={calendarOpen}
            onClose={closeCalendar}
            onDateTimeSelect={handleCalendarDateTimeSelect}
            selectedDateTime={scheduledDateTime}
            title="Schedule"
            drawerContainer={drawerContainerEl}
          />
        </DialogContent>
      </Dialog>

      {/* Simple Preview Modal (kept minimal; your PreviewPostDialog remains separate) */}
      <Dialog
        open={previewModalOpen}
        onClose={closePreviewModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: "#4B5AB6",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ScheduleIcon />
          Confirm Submission for Approval
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              Your post will be submitted for approval and will be published on{" "}
              {props.scheduledDateTime?.format("MMMM DD, YYYY at HH:mm")} after
              approval.
            </Alert>

            <Typography variant="body1" sx={{ mb: 2, whiteSpace: "pre-wrap" }}>
              {props.postContent}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {props.platforms.map((platform) => (
                <Box
                  key={platform}
                  sx={{
                    fontSize: 12,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    bgcolor: "#e0e7ff",
                    color: "#4B5AB6",
                  }}
                >
                  {platform}
                </Box>
              ))}
            </Box>

            {props.mediaFile && (
              <>
                <Typography
                  variant="caption"
                  sx={{ color: "#64748b", display: "block", mb: 1 }}
                >
                  Media Files:{" "}
                  {Array.isArray(props.mediaFile) ? props.mediaFile.length : 1}{" "}
                  {props.contentType?.toLowerCase()}
                  {(Array.isArray(props.mediaFile)
                    ? props.mediaFile.length
                    : 1) > 1
                    ? "s"
                    : ""}
                </Typography>

                {/* NEW: Thumbnails grid */}
                {mediaPreviews.length > 0 && (
                  <Grid container spacing={1} sx={{ mb: 1 }}>
                    {mediaPreviews.map((item) => (
                      <Grid item xs={4} key={`${item.kind}-${item.idx}`}>
                        <Box
                          sx={{
                            position: "relative",
                            width: "100%",
                            height: 110,
                            borderRadius: 1.5,
                            overflow: "hidden",
                            bgcolor: "#eef2ff",
                            border: "1px solid #e5e7eb",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            // Open in a new tab for quick full view without extra props
                            if (item.src)
                              window.open(
                                item.src,
                                "_blank",
                                "noopener,noreferrer"
                              );
                          }}
                          title={item.name}
                        >
                          {item.kind === "image" ? (
                            <img
                              src={item.src}
                              alt={item.name}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          ) : (
                            <video
                              src={item.src}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                display: "block",
                              }}
                              muted
                              controls={false}
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                              }}
                            />
                          )}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={props.closePreviewModal}>Cancel</Button>
          <Button
            variant="contained"
            onClick={props.confirmPosting}
            sx={{ bgcolor: "#059669", "&:hover": { bgcolor: "#10b981" } }}
          >
            Submit for Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={snackbarSeverity === "error" ? null : 6000}
        onClose={(event, reason) => {
          // For errors, only close when user clicks close button, not on clickaway
          if (snackbarSeverity === "error" && reason === "clickaway") {
            return;
          }
          handleCloseSnackbar(event, reason);
        }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "24px !important",
          "& .MuiPaper-root": {
            borderRadius: "8px",
            minWidth: "400px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          sx={{
            width: "100%",
            "& .MuiAlert-message": { fontSize: "0.95rem", fontWeight: 500 },
            "& .MuiAlert-icon": { fontSize: "24px" },
            ...(snackbarSeverity === "error" && {
              color: "#991b1b",
              backgroundColor: "#fef2f2",
              "& .MuiAlert-icon": { color: "#dc2626" },
              "& .MuiAlert-action .MuiIconButton-root": { color: "#991b1b" },
            }),
            ...(snackbarSeverity === "success" && {
              color: "#166534",
              backgroundColor: "#f0fdf4",
              "& .MuiAlert-icon": { color: "#22c55e" },
              "& .MuiAlert-action .MuiIconButton-root": { color: "#166534" },
            }),
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Image Preview Modal */}
      <Dialog
        open={imageModalOpen}
        onClose={closeImageModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          Image Preview
          <IconButton onClick={closeImageModal}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {previewSrc ? (
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                "& img": {
                  maxWidth: "100%",
                  maxHeight: "70vh",
                  borderRadius: 2,
                  objectFit: "contain",
                },
              }}
            >
              <img
                src={previewSrc}
                alt="Preview"
                onError={(e) => {
                  // Last-resort fallback if current src fails
                  const fallback =
                    (selectedImage &&
                      typeof selectedImage === "object" &&
                      (selectedImage.s3Url ||
                        selectedImage.url ||
                        selectedImage.previewUrl)) ||
                    "";
                  if (fallback && e.currentTarget.src !== fallback) {
                    e.currentTarget.src = fallback;
                  }
                }}
              />
            </Box>
          ) : (
            <Alert severity="info">No image to preview.</Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Loader Backdrop */}
      <Backdrop
        open={showLoader}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 2,
          color: "#fff",
          flexDirection: "column",
          background: "rgba(75,90,182,0.85)",
        }}
      >
        <CircularProgress color="inherit" size={60} />
        <Typography
          variant="h6"
          sx={{ mt: 3, fontWeight: 700, color: "#fff", textAlign: "center" }}
        >
          {loadingMessage || "Submitting for approval..."}
        </Typography>
        <Box sx={{ width: 220, mt: 2 }}>
          <Box
            sx={{
              height: 8,
              bgcolor: "#e0e7ff",
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                width: `${loadingProgress}%`,
                height: "100%",
                bgcolor: "#22c55e",
                transition: "width 0.4s",
              }}
            />
          </Box>
        </Box>
      </Backdrop>

      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmation}
        onClose={onCloseConfirmation}
        maxWidth="xs"
        PaperProps={{ sx: { borderRadius: 4, textAlign: "center", py: 4 } }}
      >
        <CheckCircleIcon sx={{ fontSize: 64, color: "#22c55e", mb: 2 }} />
        <DialogTitle sx={{ fontWeight: 700, color: "#059669" }}>
          Post Submitted for Approval!
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your post has been sent for approval.
            <br />
            Once approved, it will be scheduled for publishing on:
          </Typography>
          <Typography variant="h6" sx={{ color: "#4B5AB6", fontWeight: 700 }}>
            {scheduledDateTime?.format("MMMM DD, YYYY at HH:mm")}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: "#64748b" }}>
            You will be notified once it's reviewed and approved.
          </Typography>
        </DialogContent>
      </Dialog>

      {/* Error Modal for Token Expiry */}
      <Dialog
        open={errorModalOpen}
        onClose={onCloseErrorModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: "3px solid #fca5a5",
            boxShadow: "0 8px 32px rgba(220, 38, 38, 0.2)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)",
            color: "#991b1b",
            fontWeight: 700,
            fontSize: "1.5rem",
            display: "flex",
            alignItems: "center",
            gap: 2,
            borderBottom: "2px solid #fca5a5",
            pb: 2,
          }}
        >
          <ErrorOutlineIcon sx={{ fontSize: 40, color: "#dc2626" }} />
          {errorModalData?.title || "Error"}
          <IconButton
            onClick={onCloseErrorModal}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#991b1b",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Alert
            severity="error"
            icon={<WarningAmberIcon fontSize="large" />}
            sx={{
              mb: 3,
              fontSize: "0.95rem",
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            {errorModalData?.message}
          </Alert>

          <Box
            sx={{
              bgcolor: "#fef2f2",
              border: "1px solid #fca5a5",
              borderRadius: 2,
              p: 2,
              mb: 3,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <ScheduleIcon sx={{ fontSize: 28, color: "#dc2626" }} />
            <Box>
              <Typography variant="body2" sx={{ color: "#991b1b", fontWeight: 600 }}>
                Scheduled Date
              </Typography>
              <Typography variant="h6" sx={{ color: "#dc2626", fontWeight: 700 }}>
                {errorModalData?.scheduleDate || "Not specified"}
              </Typography>
            </Box>
          </Box>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: "#374151" }}>
            Affected Accounts:
          </Typography>

          <List sx={{ bgcolor: "#f9fafb", borderRadius: 2, p: 0 }}>
            {errorModalData?.accounts?.map((account, index) => (
              <React.Fragment key={index}>
                <ListItem
                  sx={{
                    py: 2,
                    "&:hover": { bgcolor: "#f3f4f6" },
                  }}
                >
                  <ListItemIcon>
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        bgcolor: "#fee2e2",
                        color: "#dc2626",
                        fontWeight: 700,
                        fontSize: "1.2rem",
                      }}
                    >
                      {account.provider?.charAt(0)}
                    </Box>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body1" sx={{ fontWeight: 600, color: "#111827" }}>
                        {account.accountName}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#6b7280",
                            display: "inline-block",
                            bgcolor: "#e5e7eb",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                            mr: 1,
                          }}
                        >
                          {account.provider}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#dc2626",
                            fontWeight: 600,
                            display: "inline-block",
                            bgcolor: "#fee2e2",
                            px: 1.5,
                            py: 0.5,
                            borderRadius: 1,
                          }}
                        >
                          Expires: {new Date(account.expiry).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < (errorModalData?.accounts?.length || 0) - 1 && (
                  <Divider variant="inset" component="li" />
                )}
              </React.Fragment>
            ))}
          </List>

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              💡 What to do next:
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>
              1. Go to <strong>Settings</strong> → <strong>Social Media Accounts</strong>
              <br />
              2. Reconnect the affected account(s)
              <br />
              3. Return here and try scheduling again
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            justifyContent: "center",
            borderTop: "1px solid #e5e7eb",
            pt: 2,
          }}
        >
          <Button
            onClick={onCloseErrorModal}
            variant="contained"
            sx={{
              bgcolor: "#dc2626",
              "&:hover": { bgcolor: "#b91c1c" },
              fontWeight: 600,
              minWidth: 120,
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SafePostDialogs;
